import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useMemo } from 'react';

import { VISITOR_ENTRY_SAVE_API, WABA_SETTINGS_API } from '../utils/api';

import BasicInfoTab from './visitortabs/BasicInfoTab';
import CompanyInfoTab from './visitortabs/CompanyInfoTab';
import IdentificationTab from './visitortabs/IdentificationTab';
import VisitInfoTab from './visitortabs/VisitInfoTab';
import SecurityTab from './visitortabs/SecurityTab';

function VisitorEntry() {
  const webcamRef = useRef(null);
  const navigate = useNavigate();

  const [photo, setPhoto] = useState(null);
  const [isWebcamReady, setIsWebcamReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wabaActive, setWabaActive] = useState(false);
  const [wabaChecked, setWabaChecked] = useState(false);

  const [form, setForm] = useState({
    name: '',
    mobile: '',
    sex: '',
    birthDate: '',
    company: '',
    companyAddress: '',
    idDocument: '',
    idNumber: '',
    visitorType: '',
    purpose: '',
    whomToVisit: '',
    allowMobile: false,
    allowLaptop: false,
    allowOthers: '',
    issuedOthers: '',
    gst: '',
    pincode: '',
    state: '',
    district: '',
    city: '',
    visitor_type_id: '',
    visit_purpose_id: '',
    securityItems: []
  });

  // ✅ Validation rules
  const requiredFields = [
    'name', 'mobile', 'sex', 'birthDate', 'company', 'companyAddress',
   
    'pincode', 'state', 'district', 'city'
  ];


const isFormValid = useMemo(() => {
  return requiredFields.every((key) => {
    const value = form[key];
    return value !== undefined && value !== null && value.toString().trim() !== '';
  }) && !!photo;
}, [form, photo]);

  // ✅ WABA fetch
  useEffect(() => {
    const fetchWaba = async () => {
      const token = localStorage.getItem('token');
      const decoded = JSON.parse(atob(token));
      const customer_id = decoded.customer_id;

      try {
        const res = await axios.post(WABA_SETTINGS_API, {
          customer_id,
          token,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.data.success && res.data.data) {
          const { active_status } = res.data.data;
          setWabaActive(active_status === 1 || active_status === '1');
        }
      } catch (err) {
        console.error('WABA check failed', err);
      } finally {
        setWabaChecked(true);
      }
    };

    fetchWaba();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const capturePhoto = () => {
    if (!isWebcamReady) {
      alert('Webcam is not ready yet. Please wait...');
      return;
    }
    const imageSrc = webcamRef?.current?.getScreenshot();
    if (imageSrc) {
      setPhoto(imageSrc);
    } else {
      alert('Could not capture image. Try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || !isFormValid) return;

    setSaving(true);
    const token = localStorage.getItem('token');
    if (!token) return alert('Token missing');

    let decoded;
    try {
      decoded = JSON.parse(atob(token));
    } catch (err) {
      alert('Invalid token');
      return;
    }

    const customer_id = decoded.customer_id;
    const user_id = decoded.id;

    const payload = {
      ...form,
      photo,
      customer_id,
      created_by: user_id,
      token,
    };

    try {
      const res = await axios.post(VISITOR_ENTRY_SAVE_API, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.data.success) {
        toast.success('Visitor entry saved successfully');
        setTimeout(() => {
          navigate('/visitor-show');
          setSaving(false);
        }, 5000);
      } else {
        toast.error(res.data.message || 'Failed to save');
        setSaving(false);
      }
    } catch (err) {
      toast.error('Error saving visitor entry');
      setSaving(false);
      console.error(err);
    }
  };

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h3 className="mb-4">📝 Visitor Entry Form</h3>
        <form onSubmit={handleSubmit} className="card shadow p-4" style={{ maxHeight: '90vh', overflowY: 'auto' }}>

          <div className="p-3 mb-4 rounded bg-light border">
            <h5 className="mb-3">📍 Basic Info</h5>
            <BasicInfoTab
              form={form}
              setForm={setForm}
              handleChange={handleChange}
              webcamRef={webcamRef}
              capturePhoto={capturePhoto}
              photo={photo}
              isWebcamReady={isWebcamReady}
              setPhoto={setPhoto}
              setIsWebcamReady={setIsWebcamReady}
              wabaActive={wabaActive}
            />
          </div>

          <div className="p-3 mb-4 rounded bg-info bg-opacity-25 border">
            <h5 className="mb-3">🏢 Company Info</h5>
            <CompanyInfoTab
              form={form}
              setForm={setForm}
              handleChange={handleChange}
            />
          </div>

          <div className="p-3 mb-4 rounded bg-warning bg-opacity-25 border">
            <h5 className="mb-3">🪪 Identification Details</h5>
            <IdentificationTab
              form={form}
              handleChange={handleChange}
            />
          </div>

          <div className="p-3 mb-4 rounded bg-success bg-opacity-25 border">
            <h5 className="mb-3">📅 Visit Information</h5>
            <VisitInfoTab
              form={form}
              setForm={setForm}
              handleChange={handleChange}
            />
          </div>

          <div className="p-3 mb-4 rounded bg-secondary bg-opacity-25 border" hidden>
            <h5 className="mb-3">🛡️ Security Items</h5>
            <SecurityTab
              form={form}
              setForm={setForm}
            />
          </div>

          <div className="d-flex justify-content-end gap-3 mt-4">
            <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={() => {
              setForm({
                name: '',
                mobile: '',
                sex: '',
                birthDate: '',
                company: '',
                companyAddress: '',
                idDocument: '',
                idNumber: '',
                visitorType: '',
                purpose: '',
                whomToVisit: '',
                allowMobile: false,
                allowLaptop: false,
                allowOthers: '',
                issuedOthers: '',
                gst: '',
                pincode: '',
                state: '',
                district: '',
                city: '',
                visitor_type_id: '',
                visit_purpose_id: '',
                securityItems: []
              });
              setPhoto(null);
            }}>
              🔄 Reset
            </button>

            <button type="submit" className="btn btn-primary" disabled={saving || !isFormValid}>
              {saving ? '⏳ Saving...' : '✅ Submit Entry'}
            </button>
          </div>

          {!isFormValid && !saving && (
            <div className="text-danger small text-end mb-4">
              ⚠️ Please fill all required fields before submitting.
            </div>
          )}
        </form>
      </div>
      <Footer />
    </>
  );
}

export default VisitorEntry;
